import React, { useRef, useState, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function snapMinute(val, step) {
  return Math.round(val / step) * step % 60;
}

function Unit({ value, min, max, step = 1, onChange, label, colors, isLight }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(null); // raw typed digits
  const inputRef = useRef();

  const display = draft !== null ? draft : String(value).padStart(2, '0');

  const increment = useCallback((dir) => {
    const range = max - min + 1;
    const next = ((value - min + dir + range) % range) + min;
    // snap to step for minutes
    if (step > 1) {
      onChange(snapMinute(next, step));
    } else {
      onChange(next);
    }
  }, [value, min, max, step, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); increment(step); }
    if (e.key === 'ArrowDown') { e.preventDefault(); increment(-step); }
    if (e.key === 'Tab') commitDraft();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    increment(e.deltaY < 0 ? step : -step);
  };

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDraft(raw);
    // Auto-commit when 2 digits entered and clearly out of range
    if (raw.length === 2) {
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num > max && num !== 24) {
        const clamped = clamp(num, min, max);
        const snapped = step > 1 ? snapMinute(clamped, step) : clamped;
        onChange(snapped);
        setDraft(null);
      }
    }
  };

  const commitDraft = () => {
    if (draft !== null) {
      const num = parseInt(draft, 10);
      if (!isNaN(num)) {
        // 24 wraps naar 00
        const resolved = num === 24 ? 0 : clamp(num, min, max);
        const snapped = step > 1 ? snapMinute(resolved, step) : resolved;
        onChange(snapped);
      }
      setDraft(null);
    }
  };

  const borderColor = focused
    ? '#f97316'
    : isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.10)';

  const bg = isLight ? '#f8f9fa' : 'rgba(255,255,255,0.05)';
  const chevronColor = isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)';
  const chevronHoverColor = '#f97316';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      {label && (
        <div style={{ fontSize: 10, fontWeight: 600, color: colors.muted, opacity: 0.5, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>
          {label}
        </div>
      )}

      {/* Up button */}
      <ChevronButton onClick={() => increment(step)} color={chevronColor} hoverColor={chevronHoverColor}>
        <ChevronUp size={16} strokeWidth={2.5} />
      </ChevronButton>

      {/* Number input */}
      <div style={{
        position: 'relative',
        width: 72,
        height: 60,
        borderRadius: 12,
        border: `2px solid ${borderColor}`,
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 0.15s',
        boxShadow: focused ? '0 0 0 3px rgba(249,115,22,0.12)' : 'none',
      }}>
        <input
          ref={inputRef}
          value={display}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
          onFocus={() => { setFocused(true); inputRef.current?.select(); }}
          onBlur={() => { setFocused(false); commitDraft(); }}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: 'transparent',
            textAlign: 'center',
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "'SF Mono', Monaco, 'Courier New', monospace",
            color: focused ? '#f97316' : colors.text,
            outline: 'none',
            cursor: 'default',
            caretColor: 'transparent',
            letterSpacing: '0.02em',
          }}
          inputMode="numeric"
        />
      </div>

      {/* Down button */}
      <ChevronButton onClick={() => increment(-step)} color={chevronColor} hoverColor={chevronHoverColor}>
        <ChevronDown size={16} strokeWidth={2.5} />
      </ChevronButton>
    </div>
  );
}

function ChevronButton({ onClick, color, hoverColor, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 32,
        height: 28,
        border: 'none',
        background: 'transparent',
        color: hovered ? hoverColor : color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: 6,
        transition: 'color 0.12s',
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

export default function TimePicker({ value = '09:00', onChange, minuteStep = 5 }) {
  const { colors, themeMode } = useTheme();
  const isLight = themeMode === 'light';

  const parts = (value || '09:00').split(':');
  const hh = clamp(parseInt(parts[0], 10) || 0, 0, 23);
  const rawMm = parseInt(parts[1], 10) || 0;
  const mm = snapMinute(rawMm, minuteStep);

  const setHour = (h) => onChange(`${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
  const setMinute = (m) => onChange(`${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`);

  const separatorColor = isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      padding: '4px 0',
    }}>
      <Unit
        value={hh}
        min={0}
        max={23}
        step={1}
        onChange={setHour}
        label="uur"
        colors={colors}
        isLight={isLight}
      />

      <div style={{
        fontSize: 28,
        fontWeight: 700,
        fontFamily: "'SF Mono', Monaco, monospace",
        color: separatorColor,
        padding: '0 6px',
        paddingBottom: 10,
        alignSelf: 'center',
        lineHeight: 1,
      }}>
        :
      </div>

      <Unit
        value={mm}
        min={0}
        max={59}
        step={minuteStep}
        onChange={setMinute}
        label="min"
        colors={colors}
        isLight={isLight}
      />
    </div>
  );
}
